import { SpriteId } from '@src/ecs/components';

export enum WeaponId {
  Wand = 0,
  SunCleaver = 1,
  LightningStaff = 2,
  PhaseCrossbow = 3,
  VoidOrb = 4,
  LifeScythe = 5,
  HydraMortar = 6,
  Cataclysm = 7,
}

export enum FirePattern {
  Bolt = 0,
  Crescent = 1,
  Beam = 2,
  PhaseBolt = 3,
  /** One-shot: damages every living enemy in the current room, then reverts to the wand. */
  Detonate = 4,
}

export interface WeaponDef {
  id: WeaponId;
  name: string;
  callout: string;
  /** Voice clip id in public/assets/voice. */
  voiceId: string;
  pattern: FirePattern;
  enabled: boolean;
  /** Seconds the weapon lasts once grabbed. 0 = never expires. */
  duration: number;
  fireRate: number;
  damage: number;
  /** Multiplier applied when the projectile hits a spawner portal. */
  spawnerMult: number;
  projectileSpeed: number;
  projectileLife: number;
  projectileRadius: number;
  knockback: number;
  pierce: number;
  /** Pattern-specific: count of projectiles per shot / beam chain count. */
  count: number;
  overchargeCount: number;
  /** Arc in degrees for Crescent, fan spread for PhaseBolt. */
  arcDeg: number;
  overchargeArcDeg: number;
  /** Beam-only: range of the initial lock and the chain hop. */
  beamRange: number;
  chainRange: number;
  pickupSprite: SpriteId;
  tint: number;
}

export const WEAPONS: readonly WeaponDef[] = [
  {
    id: WeaponId.Wand,
    name: 'Apprentice Wand',
    callout: '',
    voiceId: '',
    pattern: FirePattern.Bolt,
    enabled: true,
    duration: 0,
    fireRate: 8,
    damage: 2,
    spawnerMult: 1,
    projectileSpeed: 24,
    projectileLife: 1.4,
    projectileRadius: 0.16,
    knockback: 1.5,
    pierce: 0,
    count: 1,
    overchargeCount: 1,
    arcDeg: 0,
    overchargeArcDeg: 0,
    beamRange: 0,
    chainRange: 0,
    pickupSprite: SpriteId.BoltWand,
    tint: 0xa8f0ff,
  },
  {
    id: WeaponId.SunCleaver,
    name: 'Sun-Forged Cleaver',
    callout: 'SUN-CLEAVER! EXTRA CRISPY!',
    voiceId: 'weapon_cleaver',
    pattern: FirePattern.Crescent,
    enabled: true,
    duration: 20,
    fireRate: 2.6,
    damage: 3,
    spawnerMult: 0.5,
    projectileSpeed: 16,
    projectileLife: 0.32,
    projectileRadius: 0.34,
    knockback: 9,
    pierce: 99,
    count: 11,
    overchargeCount: 26,
    arcDeg: 180,
    overchargeArcDeg: 360,
    beamRange: 0,
    chainRange: 0,
    pickupSprite: SpriteId.PickupCleaver,
    tint: 0xff7a1a,
  },
  {
    id: WeaponId.LightningStaff,
    name: 'Lightning Staff',
    callout: 'UNLIMITED... POWER!',
    voiceId: 'weapon_lightning',
    pattern: FirePattern.Beam,
    enabled: true,
    duration: 16,
    fireRate: 0,
    damage: 14,
    spawnerMult: 0.6,
    projectileSpeed: 0,
    projectileLife: 0,
    projectileRadius: 0,
    knockback: 0.4,
    pierce: 0,
    count: 8,
    overchargeCount: 20,
    arcDeg: 0,
    overchargeArcDeg: 0,
    beamRange: 9,
    chainRange: 3.2,
    pickupSprite: SpriteId.PickupLightning,
    tint: 0x4af0ff,
  },
  {
    id: WeaponId.PhaseCrossbow,
    name: 'Phase-Arrow Crossbow',
    callout: 'PHASE SHOT! NO ESCAPE!',
    voiceId: 'weapon_crossbow',
    pattern: FirePattern.PhaseBolt,
    enabled: true,
    duration: 18,
    fireRate: 5,
    damage: 6,
    spawnerMult: 1,
    projectileSpeed: 40,
    projectileLife: 1.2,
    projectileRadius: 0.2,
    knockback: 2,
    pierce: 999,
    count: 1,
    overchargeCount: 5,
    arcDeg: 0,
    overchargeArcDeg: 40,
    beamRange: 0,
    chainRange: 0,
    pickupSprite: SpriteId.PickupCrossbow,
    tint: 0xc07aff,
  },
  {
    id: WeaponId.VoidOrb,
    name: 'Void Orb Cannons',
    callout: 'SINGULARITY! CLEAN UP AISLE THREE!',
    voiceId: 'weapon_void',
    pattern: FirePattern.Bolt,
    enabled: false,
    duration: 18,
    fireRate: 1,
    damage: 2,
    spawnerMult: 1,
    projectileSpeed: 4,
    projectileLife: 4,
    projectileRadius: 0.6,
    knockback: -6,
    pierce: 999,
    count: 1,
    overchargeCount: 3,
    arcDeg: 0,
    overchargeArcDeg: 30,
    beamRange: 0,
    chainRange: 0,
    pickupSprite: SpriteId.PickupCrossbow,
    tint: 0x8a2bff,
  },
  {
    id: WeaponId.LifeScythe,
    name: 'Life-Steal Scythe',
    callout: 'SOUL HARVEST! SLURP IT UP!',
    voiceId: 'weapon_scythe',
    pattern: FirePattern.Crescent,
    enabled: false,
    duration: 15,
    fireRate: 3,
    damage: 4,
    spawnerMult: 0.3,
    projectileSpeed: 10,
    projectileLife: 0.2,
    projectileRadius: 0.4,
    knockback: 3,
    pierce: 99,
    count: 16,
    overchargeCount: 24,
    arcDeg: 360,
    overchargeArcDeg: 360,
    beamRange: 0,
    chainRange: 0,
    pickupSprite: SpriteId.PickupCleaver,
    tint: 0x7dff5a,
  },
  {
    id: WeaponId.HydraMortar,
    name: 'Hydra Mortar',
    callout: 'HYDRA STORM! MORE EXPLOSIONS!',
    voiceId: 'weapon_hydra',
    pattern: FirePattern.Bolt,
    enabled: false,
    duration: 18,
    fireRate: 1.2,
    damage: 12,
    spawnerMult: 1,
    projectileSpeed: 12,
    projectileLife: 1,
    projectileRadius: 0.3,
    knockback: 6,
    pierce: 0,
    count: 1,
    overchargeCount: 1,
    arcDeg: 0,
    overchargeArcDeg: 0,
    beamRange: 0,
    chainRange: 0,
    pickupSprite: SpriteId.PickupCleaver,
    tint: 0xffd23a,
  },
  {
    id: WeaponId.Cataclysm,
    name: 'Cataclysm Orb',
    callout: 'CATASTROPHE! EVERYBODY DOWN!',
    voiceId: '',
    pattern: FirePattern.Detonate,
    enabled: true,
    duration: 0,
    fireRate: 20,
    damage: 240,
    /** ~48 vs a floor-1 portal (60): a big dent, not a free clear of the structure. */
    spawnerMult: 0.2,
    projectileSpeed: 0,
    projectileLife: 0,
    projectileRadius: 0,
    knockback: 10,
    pierce: 0,
    count: 1,
    overchargeCount: 1,
    arcDeg: 0,
    overchargeArcDeg: 0,
    beamRange: 0,
    chainRange: 0,
    pickupSprite: SpriteId.PickupBomb,
    tint: 0xff5a18,
  },
];

export const DROP_POOL: readonly WeaponId[] = WEAPONS.filter((w) => w.enabled && w.id !== WeaponId.Wand && w.id !== WeaponId.Cataclysm).map((w) => w.id);

export function weaponDef(id: number): WeaponDef {
  return WEAPONS[id] ?? WEAPONS[WeaponId.Wand];
}

/** Regular drop, with a rare roll for the Cataclysm Orb. */
export function rollWeaponDrop(rng: { chance(p: number): boolean; int(min: number, maxInclusive: number): number }, rareChance: number): WeaponId {
  if (rng.chance(rareChance)) return WeaponId.Cataclysm;
  if (DROP_POOL.length > 0) return DROP_POOL[rng.int(0, DROP_POOL.length - 1)];
  return WeaponId.Cataclysm;
}
